import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addAddon,
  deleteAddons,
  editAddonDetails,
  fetchAddonDetails,
  fetchAddons,
  fetchStates,
} from "../../services/services";

// Initial State
const initialState = {
  addonsData: [],
  selectedAddon: null,
  isAddonsLoading: false,
  isAddingAddon: false,
  isEditingAddon: false,
  isDeletingAddon: false,
  isFetchingAddonDetails: false,
  isError: false,
  deleteMessage: {},
  errorMessage: "",
  statesData: [],
  pagination: {
    page: 1,
    limit: 10,
  },
};

// Async Thunk for fetching addons with pagination (GET)
export const fetchAddonsAsync = createAsyncThunk(
  "addons/fetchAddons",
  async ({ page, limit, searchtext }, { rejectWithValue }) => {
    try {
      const response = await fetchAddons(page, limit, searchtext);
      return { data: response, page, limit, searchtext };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for fetching states (GET)
export const fetchStatesAsync = createAsyncThunk(
  "states/fetchStates",
  async () => {
    const response = await fetchStates();
    return response;
  }
);

// Async Thunk for fetching details of a specific addon (GET)
export const fetchAddonDetailsAsync = createAsyncThunk(
  "addons/fetchAddonDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchAddonDetails(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for adding a new addon (POST)
export const addAddonAsync = createAsyncThunk(
  "addons/addAddon",
  async (addonData, { rejectWithValue }) => {
    try {
      const response = await addAddon(addonData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for editing an existing addon (PUT)
export const editAddonAsync = createAsyncThunk(
  "addons/editAddon",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await editAddonDetails(id, updatedData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for deleting an addon (DELETE)
export const deleteAddonAsync = createAsyncThunk(
  "addons/deleteAddon",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteAddons(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Addons Slice
const addonsSlice = createSlice({
  name: "addons",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Addons
      .addCase(fetchAddonsAsync.pending, (state) => {
        state.isAddonsLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchAddonsAsync.fulfilled, (state, action) => {
        state.isAddonsLoading = false;
        state.addonsData = action.payload.data;
        state.pagination.page = action.payload.page;
        state.pagination.limit = action.payload.limit;
      })
      .addCase(fetchAddonsAsync.rejected, (state, action) => {
        state.isAddonsLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Fetch Addons
      .addCase(fetchStatesAsync.pending, (state) => {
        state.isError = false;
      })
      .addCase(fetchStatesAsync.fulfilled, (state, action) => {
        state.statesData = action.payload;
        state.isError = false;
      })
      .addCase(fetchStatesAsync.rejected, (state, action) => {
        state.isError = true;
      })

      // Fetch Addon Details
      .addCase(fetchAddonDetailsAsync.pending, (state) => {
        state.isFetchingAddonDetails = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchAddonDetailsAsync.fulfilled, (state, action) => {
        state.isFetchingAddonDetails = false;
        state.selectedAddon = action.payload;
      })
      .addCase(fetchAddonDetailsAsync.rejected, (state, action) => {
        state.isFetchingAddonDetails = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Add Addon
      .addCase(addAddonAsync.pending, (state) => {
        state.isAddingAddon = false;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(addAddonAsync.fulfilled, (state, action) => {
        state.isAddingAddon = true;
        state.addonsData = action.payload;
      })
      .addCase(addAddonAsync.rejected, (state, action) => {
        state.isAddingAddon = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Edit Addon
      .addCase(editAddonAsync.pending, (state) => {
        state.isEditingAddon = false;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(editAddonAsync.fulfilled, (state, action) => {
        state.isEditingAddon = true;
        state.selectedAddon = action.payload;
      })
      .addCase(editAddonAsync.rejected, (state, action) => {
        state.isEditingAddon = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Delete Addon
      .addCase(deleteAddonAsync.pending, (state) => {
        state.isDeletingAddon = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(deleteAddonAsync.fulfilled, (state, action) => {
        state.isDeletingAddon = false;
        state.addonsData = state?.addonsData?.addons.filter(
          (addon) => addon.id !== action.payload.id
        );
      })
      .addCase(deleteAddonAsync.rejected, (state, action) => {
        state.isDeletingAddon = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

// Export reducer
export default addonsSlice.reducer;
